// client/src/components/pages/LexiStar/LiveView/LiveView.tsx
//
// Live View (/lexistar/live, sidebar "LexiStar · Operate", page permission geofencing.view):
// where every truck and loader is right now. The vehicle rail, the live map and the vehicle
// detail panel, with the All | Fleet | Loaders filter, the zone-layer chips and full screen.
// ST-00210: this was the Live Map tab of the Geofences page; the components are the same ones
// (Geofencing/LiveTrackingMap, VehicleRail, VehicleDetailPanel) and so is the feed
// (Geofencing/useTrackingFeed: the /tracking socket plus the 30 s fallback polls). The filter
// and layer choices keep their storage keys, so a user's choices carry over.
// ST-00253 / ST-00244: every vehicle's colour on the map, the rail and the detail panel is its
// LexiStar status board key (the same as on the Status Board and Replay, map/assetStatus) when it
// has one, else its motion (useLexiStarStatusKeys, vehicleStatus liveStatusOf).
// ST-00276 phase 2 (map colours & states): the colour, words and overlay marks are the vehicle's
// display — its status board row's (the organisation's display rules, evaluated by the server),
// else the organisation's rules over its motion — on the map, the rail, the card and the panel;
// the map key is generated from the rules (useLexiStarDisplays, map/assetDisplay).
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaCompress, FaExpand, FaSatelliteDish } from 'react-icons/fa';
import LiveTrackingMap from '../../Geofencing/LiveTrackingMap';
import VehicleRail from '../../Geofencing/VehicleRail';
import VehicleDetailPanel from '../../Geofencing/VehicleDetailPanel';
import { useLexiStarDisplays } from '../../Geofencing/useLexiStarStatusKeys';
import { compiledOf, displayOfMotion } from '../../../map/assetDisplay';
import { liveStatusOf, normaliseRegistration } from '../../Geofencing/vehicleStatus';
import GeofenceStatsCards from '../../Geofencing/GeofenceStatsCards';
import useTrackingFeed from '../../Geofencing/useTrackingFeed';
import { OPEN_ALERTS_PATH, geofencingPath } from '../../Geofencing/geofencingPaths';
import { tripsTabPath } from '../Replay/replayPaths';
import { ZONE_TYPES, getZoneType as getZoneTypeJs } from '../../Geofencing/zoneTypes';
import { PageHeader, SegmentedControl } from '../../../../types/commonComponents';
import type {
  FleetVehicle,
  GeofenceRow,
} from '../../../../types/clientPages/geofencing';

// Typed views of the JavaScript modules this page uses.
const getZoneType = getZoneTypeJs as unknown as (zone: GeofenceRow | null | undefined) => string;

type ClassFilter = 'all' | 'fleet' | 'loaders';

// LexiStar asset-class sections — filters VEHICLES ONLY (Fleet = trucks,
// Loaders = site loaders). Zone visibility is controlled separately by the
// per-zone-type layer toggles: any vehicle class can visit any zone type.
const CLASS_FILTERS: { id: ClassFilter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'fleet', label: 'Fleet' },
  { id: 'loaders', label: 'Loaders' },
];
// The keys the Live Map tab used, so stored choices carry over to this page.
export const CLASS_FILTER_STORAGE_KEY = 'lexistar.classFilter';
export const ZONE_LAYERS_STORAGE_KEY = 'lexistar.zoneLayers';

/**
 * Where the cards lead: the Geofences page's Events tab — on Open alerts for Pending Alerts.
 * Both counts are LexiStar's zone events since ST-00269 (useTrackingFeed).
 */
const EVENTS_PATH = geofencingPath('events');

const loadClassFilter = (): ClassFilter => {
  try {
    const stored = localStorage.getItem(CLASS_FILTER_STORAGE_KEY);
    return CLASS_FILTERS.some((f) => f.id === stored) ? (stored as ClassFilter) : 'all';
  } catch {
    return 'all';
  }
};

// All zone-type layers default to visible; stored preferences overlay that.
const defaultZoneLayers = (): Record<string, boolean> =>
  Object.fromEntries(ZONE_TYPES.map((t) => [t.id, true]));

const loadZoneLayers = (): Record<string, boolean> => {
  const layers = defaultZoneLayers();
  try {
    // getItem's null parses to null (no stored preference).
    const stored: unknown = JSON.parse(localStorage.getItem(ZONE_LAYERS_STORAGE_KEY) as string);
    if (stored && typeof stored === 'object') {
      const prefs = stored as Record<string, unknown>;
      ZONE_TYPES.forEach((t) => {
        const pref = prefs[t.id];
        if (typeof pref === 'boolean') layers[t.id] = pref;
      });
    }
  } catch {
    /* corrupt/unavailable storage — use defaults */
  }
  return layers;
};

const VEHICLES_LABEL: Record<ClassFilter, string> = {
  all: 'Vehicles Online',
  fleet: 'Trucks Online',
  loaders: 'Loaders Online',
};

export default function LiveView() {
  const navigate = useNavigate();
  const {
    geofences,
    vehicles,
    fleet,
    fleetLoading,
    unacknowledgedCount,
    eventsToday,
    loading,
    loadedOnce,
  } = useTrackingFeed({ live: true });
  // LexiStar status keys and displays by registration (refreshed every 30 s): the vehicles'
  // colours and marks, and the organisation's display rules (legend; vehicles without a row).
  const { keys: statusKeys, displays, rules: displayRules } = useLexiStarDisplays();
  const compiled = useMemo(
    () => (displayRules ? compiledOf(displayRules.rules, displayRules.version) : null),
    [displayRules]
  );
  const statusOf = useCallback(
    (v: FleetVehicle) => {
      const reg = normaliseRegistration(v.regNo);
      const display =
        displays.get(reg) ?? (compiled ? displayOfMotion(compiled, v.status, v.speed ?? null) : null);
      return liveStatusOf(v.status, statusKeys.get(reg), display);
    },
    [statusKeys, displays, compiled]
  );

  const [classFilter, setClassFilter] = useState<ClassFilter>(loadClassFilter);
  const [zoneLayers, setZoneLayers] = useState(loadZoneLayers);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [selectedRegNo, setSelectedRegNo] = useState<string | null>(null);
  // ST-00270: a marker click selects the vehicle and opens its card on the map; the detail panel
  // opens from the card's Details button, or straight away from the list.
  const [detailOpen, setDetailOpen] = useState(false);
  const [flyToNonce, setFlyToNonce] = useState(0); // bump to (re)center the map

  // Persist the Fleet/Loaders section choice
  useEffect(() => {
    try {
      localStorage.setItem(CLASS_FILTER_STORAGE_KEY, classFilter);
    } catch {
      /* storage unavailable */
    }
  }, [classFilter]);

  // Persist the per-zone-type layer toggles
  useEffect(() => {
    try {
      localStorage.setItem(ZONE_LAYERS_STORAGE_KEY, JSON.stringify(zoneLayers));
    } catch {
      /* storage unavailable */
    }
  }, [zoneLayers]);

  // Filter live vehicles by class (Fleet = truck, Loaders = loader). Positions
  // arriving via the socket update `vehicles` and flow through here too.
  // Missing vehicleClass defaults to 'loader', matching the live endpoint.
  // This is the ONLY thing the All | Fleet | Loaders control filters.
  const filteredVehicles = useMemo(() => {
    if (classFilter === 'all') return vehicles;
    const target = classFilter === 'fleet' ? 'truck' : 'loader';
    return vehicles.filter((v) => (v.vehicleClass || 'loader') === target);
  }, [vehicles, classFilter]);

  // The rail mirrors the map's class filter over the richer /fleet set. The
  // server already sorts moving→offline; preserve that order.
  const filteredFleet = useMemo(() => {
    if (classFilter === 'all') return fleet;
    const target = classFilter === 'fleet' ? 'truck' : 'loader';
    return fleet.filter((v) => (v.vehicleClass || 'loader') === target);
  }, [fleet, classFilter]);

  // Selected vehicle's full record for the detail panel (null when none/absent).
  const selectedVehicle = useMemo(
    () => (selectedRegNo ? fleet.find((v) => v.regNo === selectedRegNo) || null : null),
    [fleet, selectedRegNo]
  );

  // From the list: select, open the details and (re)center the map.
  const handleSelectVehicle = useCallback((regNo: string) => {
    setSelectedRegNo(regNo);
    setDetailOpen(true);
    setFlyToNonce((n) => n + 1);
  }, []);

  const handleCenterOnMap = useCallback((regNo: string) => {
    if (regNo) setSelectedRegNo(regNo);
    setFlyToNonce((n) => n + 1);
  }, []);

  // From a map marker: select and (re)center; the card on the map offers Details.
  const handleMapSelect = useCallback((regNo: string) => {
    setSelectedRegNo(regNo);
    setFlyToNonce((n) => n + 1);
  }, []);

  const handleOpenDetails = useCallback((regNo: string) => {
    setSelectedRegNo(regNo);
    setDetailOpen(true);
  }, []);

  const handleCloseDetail = useCallback(() => {
    setDetailOpen(false);
    setSelectedRegNo(null);
  }, []);

  // Detail panel → Replay's Trips tab, opened on this vehicle (ST-00318; was the Geofences page's).
  const handleViewTrips = useCallback(
    (regNo: string) => navigate(tripsTabPath({ vehicle: regNo })),
    [navigate]
  );

  // If the class filter hides the selected vehicle, drop the selection.
  useEffect(() => {
    if (selectedRegNo && !filteredFleet.some((v) => v.regNo === selectedRegNo)) {
      setSelectedRegNo(null);
      setDetailOpen(false);
    }
  }, [filteredFleet, selectedRegNo]);

  // Zone-type chips: only render toggles for types that exist in the loaded
  // zones, in taxonomy order.
  const presentZoneTypes = useMemo(() => {
    const present = new Set(geofences.map(getZoneType));
    return ZONE_TYPES.filter((t) => present.has(t.id));
  }, [geofences]);

  // Zone visibility is driven by the per-zone-type layer toggles alone —
  // independent of the vehicle class filter. Unknown/missing types stay
  // visible unless explicitly toggled off.
  const filteredGeofences = useMemo(
    () => geofences.filter((g) => zoneLayers[getZoneType(g)] !== false),
    [geofences, zoneLayers]
  );

  const toggleZoneLayer = (typeId: string) => {
    setZoneLayers((prev) => ({ ...prev, [typeId]: prev[typeId] === false }));
  };

  const toggleFullscreen = () => setIsFullscreen((prev) => !prev);

  return (
    <div
      className={
        isFullscreen
          ? 'fixed inset-0 z-50 flex flex-col bg-surface [--page-pad-x:0px]'
          : 'min-h-screen bg-slate-50'
      }
    >
      <PageHeader
        icon={FaSatelliteDish}
        title="Live View"
        subtitle="Where your trucks and loaders are right now"
        toolbar={
          /* Fleet / Loaders section toggle — filters vehicles only */
          <SegmentedControl
            ariaLabel="Vehicle class filter"
            value={classFilter}
            onChange={setClassFilter}
            options={CLASS_FILTERS.map((f) => ({ value: f.id, label: f.label }))}
          />
        }
        actions={
          <button
            type="button"
            onClick={toggleFullscreen}
            aria-label={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
            title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
            className="inline-flex items-center justify-center gap-2 h-10 min-w-10 px-2.5 sm:px-3.5 border border-slate-300 bg-surface text-slate-700 hover:border-slate-400 hover:bg-slate-50 rounded-lg text-sm font-medium whitespace-nowrap transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
          >
            {isFullscreen ? (
              <FaCompress className="w-4 h-4 text-slate-500" aria-hidden="true" />
            ) : (
              <FaExpand className="w-4 h-4 text-slate-500" aria-hidden="true" />
            )}
          </button>
        }
      />

      <div
        className={`max-w-[1800px] w-full mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-6 ${
          isFullscreen ? 'flex-1 min-h-0 flex flex-col' : ''
        }`}
      >
        {/* Zone layer toggles — one chip per zone type present in loaded zones */}
        {presentZoneTypes.length > 0 && (
          <div
            className="flex flex-wrap items-center gap-1.5 mb-4"
            role="group"
            aria-label="Zone layer toggles"
          >
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider mr-1">
              Zone layers
            </span>
            {presentZoneTypes.map((t) => {
              const isOn = zoneLayers[t.id] !== false;
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => toggleZoneLayer(t.id)}
                  aria-pressed={isOn}
                  title={isOn ? `Hide ${t.label.toLowerCase()}` : `Show ${t.label.toLowerCase()}`}
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold rounded-full border transition-all ${
                    isOn
                      ? 'bg-surface text-slate-700 border-slate-300 shadow-sm'
                      : 'bg-slate-100 text-slate-400 border-transparent hover:text-slate-600'
                  }`}
                >
                  <span
                    className="inline-block w-2 h-2 rounded-full flex-shrink-0"
                    style={{ background: isOn ? t.color : 'var(--border-strong)' }}
                  />
                  {t.label}
                </button>
              );
            })}
          </div>
        )}

        {loading && !loadedOnce && (
          <div role="status" className="flex items-center gap-2 mb-4 text-sm text-slate-500">
            <span
              className="inline-block w-4 h-4 border-2 border-slate-300 border-t-transparent rounded-full animate-spin"
              aria-hidden="true"
            />
            Loading vehicles and zones...
          </div>
        )}

        {/* Stats — the alert cards open the Geofences page's Events tab */}
        <GeofenceStatsCards
          stats={['vehicles', 'unacknowledged', 'eventsToday']}
          vehiclesLabel={VEHICLES_LABEL[classFilter]}
          vehicleCount={filteredVehicles.length}
          unacknowledgedCount={unacknowledgedCount}
          eventsToday={eventsToday}
          links={{ unacknowledged: OPEN_ALERTS_PATH, eventsToday: EVENTS_PATH }}
        />

        <div className={`mt-4 ${isFullscreen ? 'flex-1 min-h-0' : 'min-h-[500px]'}`}>
          <div className={`flex flex-col lg:flex-row gap-3 lg:gap-4 ${isFullscreen ? 'h-full' : ''}`}>
            <VehicleRail
              vehicles={filteredFleet}
              loading={fleetLoading}
              selectedRegNo={selectedRegNo}
              onSelect={handleSelectVehicle}
              statusOf={statusOf}
            />
            <div className={`flex-1 min-w-0 ${isFullscreen ? 'min-h-0 h-full' : ''}`}>
              <LiveTrackingMap
                vehicles={filteredVehicles}
                geofences={filteredGeofences}
                isFullscreen={isFullscreen}
                overlaysVersion={0}
                selectedRegNo={selectedRegNo}
                onSelectVehicle={handleMapSelect}
                onOpenDetails={handleOpenDetails}
                flyToNonce={flyToNonce}
                statusKeys={statusKeys}
                displays={displays}
                displayRules={displayRules}
              />
            </div>
            <VehicleDetailPanel
              vehicle={detailOpen ? selectedVehicle : null}
              onClose={handleCloseDetail}
              onViewTrips={handleViewTrips}
              onCenter={handleCenterOnMap}
              status={selectedVehicle ? statusOf(selectedVehicle) : null}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
